import React, { useState, useEffect } from "react"; 
import { createAccount, updateSettings } from "../services/FirestoreService";
import logo from "../assets/trapoundtop.png";
import { doc, getDoc } from "firebase/firestore"; // Fixes 'doc' and 'getDoc' not defined
import { db } from "../firebase"; // Fixes 'db' not defined


const TYPES = [
  { value: "Futures", label: "Futures", color: "bg-blue-600" },
  { value: "Forex", label: "Forex", color: "bg-green-600" },
  { value: "Stocks", label: "Stocks", color: "bg-purple-600" },
  { value: "Options", label: "Options", color: "bg-orange-600" },
  { value: "Mixed", label: "Mixed", color: "bg-yellow-500" },
];

export default function CreateAccountModal({ userId, accountCount, isDemo = false, isAdmin = false, onAccountCreated, onClose }) {
  // --- STATE DEFINITIONS ---
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  
  const [showPropReminder, setShowPropReminder] = useState(false);
  const [createdAccountId, setCreatedAccountId] = useState(null);

  // Mutually exclusive prop types
  const [isPropFirm, setIsPropFirm] = useState(false);       // Prop Evaluation
  const [isPropFunded, setIsPropFunded] = useState(false);   // Prop Funded
  const [isBacktest, setIsBacktest] = useState(false);

  // Step 3 inputs
  const [initBalanceInput, setInitBalanceInput] = useState("");
  const [accountGoalInput, setAccountGoalInput] = useState("");
  const [maxLossInput, setMaxLossInput] = useState("");

  // UI-only state that drives the two internal flags
  const [fundingType, setFundingType] = useState("retail");

  const maxAccounts = isDemo ? 3 : 15;
  const isTradingAccount = ["Futures", "Forex", "Stocks", "Options", "Mixed"].includes(type);
  const isAnyProp = isPropFirm || isPropFunded;

  const FUNDING_OPTIONS = [
    { id: "retail",     label: "Retail",        desc: "Personal account" },
    { id: "backtest",   label: "Backtesting",   desc: "Simulated account" },
    { id: "evaluation", label: "Prop Evaluation", desc: "Evaluation phase" },
    { id: "funded",     label: "Prop Funded",     desc: "Live funded account" },
  ];

  // --- HOOKS ---




  // 2. Keep fundingType in a valid state when account type changes

  useEffect(() => {
    if (!isTradingAccount) {
      setFundingType("retail");
    } else if (type === "Mixed") {
      setFundingType("retail");
    } else if ((type === "Stocks" || type === "Options") && !["retail", "backtest"].includes(fundingType)) {
      setFundingType("retail");
    }
  }, [type, isTradingAccount, fundingType]);

  
  // 3. Mirror UI state into your existing booleans
  useEffect(() => {
    setIsPropFunded(fundingType === "funded");
    setIsPropFirm(fundingType === "evaluation");
    setIsBacktest(fundingType === "backtest");
  }, [fundingType]);

// NEW: Prop Rules Reminder Modal
  if (showPropReminder) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 sm:p-10 shadow-xl min-w-[340px] max-w-md border-2 border-yellow-400">
          <h2 className="text-xl font-bold mb-6 text-center text-gray-900 dark:text-white">
            {isPropFunded ? "Important Funded Account Reminder" : "Important Prop Challenge Reminder"}
          </h2>
          
          <div className="mb-8 text-gray-700 dark:text-gray-300 text-sm space-y-4 text-left">
            {isPropFunded ? (
              <>
                <p className="font-bold text-green-600 dark:text-green-400 text-center text-base mb-2">
                  🎉 Congratulations on getting funded!
                </p>
                <p>Before you start trading, ensure you fully understand your firm's payout rules:</p>
                <ul className="list-disc pl-6 space-y-2 font-medium">
                  <li>Review your <strong>consistency constraints</strong>.</li>
                  <li>Verify your <strong>withdrawal windows</strong>.</li>
                  <li>Monitor your strict <strong>drawdown limits</strong>.</li>
                </ul>
                <p className="text-red-500 dark:text-red-400 font-bold text-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  Violating these rules puts your hard-earned payout at risk.
                </p>
              </>
            ) : (
              <>
                <p>Before you start trading, please ensure you fully understand all the rules of your prop challenge:</p>
                <ul className="list-disc pl-6 space-y-2 font-medium">
                  <li>Review your <strong>maximum drawdown limits</strong>.</li>
                  <li>Understand the <strong>consistency rules</strong>.</li>
                  <li>Check for any <strong>news trading restrictions</strong>.</li>
                </ul>
                <p className="font-bold text-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  Protect your account by staying within the guidelines.
                </p>
              </>
            )}
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                setShowPropReminder(false);
                if (onAccountCreated) onAccountCreated(createdAccountId);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-xl shadow transition w-full"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Account Limit Check
  if (accountCount >= maxAccounts) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-10 shadow-xl min-w-[340px] text-center border-2 border-blue-300">
          <img
            src={logo}
            alt="Trapound Logo"
            className="mx-auto mb-4"
            style={{ height: "120px", maxHeight: "180px", objectFit: "contain" }}
          />
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
            You are limited to {maxAccounts} accounts.
          </h2>
          <p className="mb-8 text-gray-700 dark:text-gray-300">
            Please delete an account before creating a new one.
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2 rounded-xl shadow transition"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // --- HELPER FUNCTIONS ---

  const handleTypeSelect = (t) => {
    setType(t);
    setStep(2);
    setError("");
    setName(`${t} Account`);
  };

  const validNumber = (v) => {
    if (v === "" || v === null || v === undefined) return false;
    const n = Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n);
  };

  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const handleCreate = async () => {
    try {
      setError("");
      if (!type) {
        setError("Please choose an account type.");
        return;
      }
      if (!name.trim()) {
        setError("Please enter a name.");
        return;
      }

      if (step === 3 && isTradingAccount) {
        if (isAnyProp) {
          if (!validNumber(initBalanceInput) || !validNumber(accountGoalInput) || !validNumber(maxLossInput)) {
            setError("Please provide valid numbers for Initial Balance, Account Goal, and Maximum Loss Amount.");
            return;
          }
        } else {
          // Retail: require both Initial Balance and Account Goal
          if (!validNumber(initBalanceInput) || !validNumber(accountGoalInput)) {
            setError("Please provide valid numbers for Initial Balance and Account Goal.");
            return;
          }
        }
      }

      setCreating(true);

      const meta = {
        isPropFirm,
        isPropFunded,
        ...(fundingType === "backtest" ? { tag: "Backtesting", isBacktest: true } : {}),
      };

      const id = await createAccount(userId, name.trim(), type, meta);

      if (isTradingAccount) {
        if (isAnyProp) {
          const initialBalance = toNum(initBalanceInput);
          const accountGoal = toNum(accountGoalInput);
          const maximumLossAmount = toNum(maxLossInput);

          const isFunded = isPropFunded === true;
          const runningBalance = initialBalance;
          const profitTarget = accountGoal - initialBalance;
          const remainingProfitAmount = profitTarget;

          const maximumLossLimit = isFunded
            ? -Math.abs(maximumLossAmount)
            : initialBalance - maximumLossAmount;

          const propInitial = isFunded
            ? Math.abs(maximumLossAmount)
            : initialBalance - maximumLossLimit;

          const propRunning = propInitial;

          const propAccountGoal = isFunded
            ? accountGoal + Math.abs(maximumLossAmount)
            : accountGoal - initialBalance + Math.abs(maximumLossAmount);

          const settings = {
            isPropFirm,
            isPropFunded,
            ...(fundingType === "backtest" ? { tag: "Backtesting", isBacktest: true } : {}),
            "Initial Balance": initialBalance,
            "Account Goal": accountGoal,
            "Running Balance": runningBalance,
            "Maximum Loss Amount": maximumLossAmount,
            "Maximum Loss Limit": maximumLossLimit,
            "Profit Target": profitTarget,
            "Remaining Profit Amount": remainingProfitAmount,
            "Prop Initial Balance": propInitial,
            "Prop Running Balance": propRunning,
            "Prop Account Goal": propAccountGoal,
            // defaults
            "Stop Loss": 25,
            "Take Profit": 50,
            "Daily Goal": 500,
            "Daily Loss Limit": 200,
            "Risk Percentage": 3.0,
            "Maximum Contracts": 50,
            "Drawdown Limit %": 20,
            "Max Trades Per Day": 10,
            "Max Loss Trades Per Day": 5,
          };

          await updateSettings(userId, id, settings);
        } else {
          const initialBalance = toNum(initBalanceInput);
          const accountGoal = toNum(accountGoalInput);
          const profitTarget = Math.max(accountGoal - initialBalance, 0);
          const remainingProfitAmount = profitTarget;

          const defaultStopLoss = (type === "Stocks" || type === "Options") ? 0.50 : 25;
          const defaultTakeProfit = (type === "Stocks" || type === "Options") ? 1.00 : 50;
          const defaultMaxContracts = type === "Stocks" ? 1000 : 50;

          const retailSettings = {
            isPropFirm: false,
            isPropFunded: false,
             ...(fundingType === "backtest" ? { tag: "Backtesting", isBacktest: true } : {}),
             isBacktest,
            "Initial Balance": initialBalance,
            "Account Goal": accountGoal,
            "Running Balance": initialBalance,
            "Profit Target": profitTarget,
            "Remaining Profit Amount": remainingProfitAmount,
            "Stop Loss": defaultStopLoss,
            "Take Profit": defaultTakeProfit,
            "Daily Goal": 500,
            "Daily Loss Limit": 200,
            "Risk Percentage": 3.00,
            "Maximum Contracts": defaultMaxContracts,
            "Drawdown Limit %": 20,
            "Max Trades Per Day": 10,
            "Max Loss Trades Per Day": 5,
          };
          await updateSettings(userId, id, retailSettings);
        }
      }

      if (isAnyProp) {
        const hasSeenReminder = localStorage.getItem("hasSeenPropReminder");
        if (!hasSeenReminder) {
          localStorage.setItem("hasSeenPropReminder", "true");
          setCreatedAccountId(id); // Store ID to pass back after they close the popup
          setShowPropReminder(true);
        } else {
          if (onAccountCreated) onAccountCreated(id);
        }
      } else {
        if (onAccountCreated) onAccountCreated(id);
      }
    } catch (e) {
      console.error(e);
      setError("Failed to create account.");
    } finally {
      setCreating(false);
    }
  };

  async function handleNextFromStep2() {
    if (isTradingAccount) {
      setStep(3);
      return;
    }
    await handleCreate();
  }

  // Update the visibleTypes filter
  const visibleTypes = isAdmin 
    ? TYPES 
    : TYPES.filter(t => t.value !== "Blank" && t.value !== "Stocks" && t.value !== "Options");

  // --- RENDER ---
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-300 bg-opacity-80 dark:bg-gray-900 dark:bg-opacity-100">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-10 shadow-xl min-w-[340px] text-center border-2 border-blue-300">
        <img
          src={logo}
          alt="Trapound Logo"
          className="mx-auto mb-4"
          style={{ height: "120px", maxHeight: "180px", objectFit: "contain" }}
        />
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          {step === 1
            ? (accountCount === 0 ? "Create Your First Journaling Account" : "Create a Journaling Account")
            : step === 2
              ? "Name Your Account"
              : (isAnyProp ? (isPropFunded ? "Prop Funded Setup" : "Prop Evaluation Setup") : "Initial Setup")}
        </h2>

        {step === 1 && (
          <>
            <p className="mb-6 text-gray-700 dark:text-gray-300">What type of account?</p>
            <div className="flex flex-wrap gap-4 justify-center mb-2">
              {visibleTypes.map(({ value, label, color }) => (
                <button
                  key={value}
                  onClick={() => handleTypeSelect(value)}
                  className={`px-8 py-3 font-bold text-white rounded-xl shadow transition ${color} hover:brightness-90`}
                  disabled={creating}
                >
                  {label}
                </button>
              ))}
            </div>
            {accountCount > 0 && (
              <div className="mt-8">
                <button
                  onClick={onClose}
                  className="text-sm text-gray-500 hover:underline"
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <input
              className={`w-full px-4 py-2 rounded-xl border ${
                !name.trim() && error ? "border-red-500" : "border-gray-300"
              } focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-800 dark:text-white mb-2`}
              value={name}
              placeholder={`E.g. ${type || "My"} 1`}
              onChange={e => setName(e.target.value)}
              autoFocus
              required
              disabled={creating}
            />
            {!name.trim() && (
              <p className="text-red-500 text-sm mb-4">Account Name is required.</p>
            )}

            {/* Funding type segmented control */}
            <div className="text-left mb-2">
              <label className="mt-6 block text-normal font-medium text-gray-700 dark:text-gray-200 mb-2">
                Funding Type
              </label>

              <div
                role="radiogroup"
                aria-label="Funding Type"
                className={`grid gap-2 ${
                  type === "Mixed" 
                    ? "grid-cols-1 max-w-[220px] mx-auto text-left" 
                    : "grid-cols-1 sm:grid-cols-2"
                }`}
              >
                {FUNDING_OPTIONS.filter(opt => {
                  // For Mixed, only show Retail
                  if (type === "Mixed") {
                    return opt.id === "retail";
                  }
                  // For Stocks or Options, only show Retail and Backtest
                  if (type === "Stocks" || type === "Options") {
                    return ["retail", "backtest"].includes(opt.id);
                  }
                  return true;
                }).map((opt) => {
                  const selected = fundingType === opt.id;
                  const disabled = creating || !isTradingAccount;

                  return (
                    <label
                      key={opt.id}
                      className={[
                        "cursor-pointer select-none rounded-xl border px-4 py-3 transition",
                        selected
                          ? "border-blue-600 ring-2 ring-blue-600 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-300 hover:border-blue-500",
                        disabled ? "opacity-50 cursor-not-allowed" : "",
                      ].join(" ")}
                    >
                      <input
                        type="radio"
                        name="funding-type"
                        value={opt.id}
                        checked={selected}
                        onChange={() => setFundingType(opt.id)}
                        disabled={disabled}
                        className="sr-only"
                      />
                      <div className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className={[
                            "mt-1 h-4 w-4 rounded-full border",
                            selected ? "bg-blue-600 border-blue-600" : "border-gray-400",
                          ].join(" ")}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {opt.label}
                          </span>
                          <span className="block text-xs text-gray-600 dark:text-gray-400">
                            {opt.desc}
                          </span>
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              {!isTradingAccount && (
                <p className="mt-2 text-xs text-gray-500">
                  Funding types are available for Futures, Forex, Stocks, &amp; Options only. This will be treated as a Retail account.
                </p>
              )}
            </div>

            <button
              onClick={handleNextFromStep2}
              disabled={creating || !name.trim()}
              className={`bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2 rounded-xl shadow transition mt-6 mb-2 ${
                (!name.trim() || creating) ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isTradingAccount ? "Next" : (creating ? "Creating..." : "Create")}
            </button>

            <div className="flex items-center justify-center gap-5 mt-3">
              <button
                onClick={() => { setStep(1); setType(""); setError(""); }}
                disabled={creating}
                className="text-xs text-gray-500 hover:underline"
              >
                Back
              </button>
              {accountCount > 0 && (
                <button
                  onClick={onClose}
                  className="text-xs text-gray-500 hover:underline"
                  disabled={creating}
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
              {isPropFunded
                ? "Enter your funded account parameters."
                : isPropFirm
                  ? "Enter your prop firm evaluation parameters."
                  : "Enter your starting balance and account goal."}
            </p>
            <div className="grid grid-cols-1 gap-4 text-left">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Initial Balance
                </label>
                <input
                  type="number"
                  value={initBalanceInput}
                  onChange={e => setInitBalanceInput(e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-800 dark:text-white"
                  placeholder={
                    isAnyProp
                      ? (type === 'Futures' ? 'e.g., 50000' : type === 'Forex' ? 'e.g., 10000' : '')
                      : (type === 'Futures' ? 'e.g., 2000'  : type === 'Forex' ? 'e.g., 2000'  : (type === 'Stocks' || type === 'Options' || type === 'Mixed') ? 'e.g., 5000' : '')
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Goal
                </label>
                <input
                  type="number"
                  min="0"
                  value={accountGoalInput}
                  onChange={e => setAccountGoalInput(e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-800 dark:text-white"
                  placeholder={
                    isAnyProp
                      ? (type === 'Futures' ? 'e.g., 53000' : type === 'Forex' ? 'e.g., 11000' : '')
                      : (type === 'Futures' ? 'e.g., 5000'  : type === 'Forex' ? 'e.g., 5000'  : (type === 'Stocks' || type === 'Options' || type === 'Mixed') ? 'e.g., 10000' : '')
                  }
                />
              </div>

              {isAnyProp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Maximum Loss Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={maxLossInput}
                    onChange={e => setMaxLossInput(e.target.value)}
                    className="no-spin w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-800 dark:text-white"
                    placeholder={type === 'Futures' ? 'e.g., 2000' : type === 'Forex' ? 'e.g., 1000' : ''}
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleCreate}
              disabled={creating}
              className={`mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2 rounded-xl shadow transition ${
                creating ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {creating ? "Creating..." : "Create"}
            </button>

            <div className="flex items-center justify-center gap-5 mt-3">
              <button
                onClick={() => setStep(2)}
                disabled={creating}
                className="text-xs text-gray-500 hover:underline"
              >
                Back
              </button>
              <button
                onClick={onClose}
                disabled={creating}
                className="text-xs text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {error && <div className="text-red-600 mt-4">{error}</div>}
      </div>
    </div>
  );
}